import * as THREE from 'three';
import { FeatureLine } from './FeatureLine';
import { resolveCircularGeometryFromSamplePoints } from '../../utils/geometry';

/** 圆参数经过世界变换后允许的相对正交、等半径误差。 */
const CIRCLE_AXIS_RELATIVE_TOLERANCE = 1e-6;

/**
 * 判断对象是否为特征曲线对象。
 *
 * @param value 待判断对象。
 * @returns 是否为特征曲线对象。
 */
export function isFeatureCurve(value: unknown): value is FeatureCurve {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeatureCurve') === true;
}

/**
 * 特征曲线对象。
 *
 * 设计说明：
 * 1. 继承 `FeatureLine` 以保持现有边选择系统兼容；
 * 2. 额外承载 worker 解析出的底层曲线类型；
 * 3. 不持有 Puppy CAD 内核 handle，只保存可序列化曲线语义，避免主线程依赖 worker 资源生命周期。
 */
export class FeatureCurve extends FeatureLine {
  /** 运行时类型标记。 */
  public readonly isFeatureCurve: boolean = true;

  /** 底层曲线类型。 */
  public readonly curveKind: TopologyCurveKind;

  /**
   * 构造函数。
   *
   * @param input 构造输入。
   */
  public constructor(input: PuppyCAD.FeatureCurveInput) {
    super(input.geometry);
    this.curveKind = input.curveKind ?? 'other';
    this.name = `FeatureCurve:${this.curveKind}`;
  }

  /**
   * 读取圆形边的世界圆几何，完整圆和裁剪圆弧共享底层圆轴。
   * 优先读取权威拓扑圆参数，避免小圆弧因显示采样不足而失去方向；没有解析参数时才恢复采样圆。
   * 本方法不覆写直线的端点方向语义，调用方必须显式选择圆轴能力。
   * @returns 圆心、单位圆轴及半径；非圆、退化或变换后成为椭圆的边返回 null。
   */
  public getWorldCircleGeometry(): PuppyCAD.CircularGeometry3D | null {
    if (this.curveKind !== 'circle') return null;
    const conic = this.edgeIndex === null ? undefined
      : this.getOwningResultMesh()?.getTopologyEdgeSnapshotByIndex(this.edgeIndex)?.exactConic;
    if (!conic) return resolveCircularGeometryFromSamplePoints(this.getWorldSamplePoints());

    // 解析参数和显示点使用同一宿主变换，未挂载的短命代理也必须跟随当前装配姿态。
    const worldMatrix = this.resolveWorldMatrixForEndpoints();
    const linearMatrix = new THREE.Matrix3().setFromMatrix4(worldMatrix);
    const center = new THREE.Vector3(...conic.center).applyMatrix4(worldMatrix);
    const xAxis = new THREE.Vector3(...conic.xVector).applyMatrix3(linearMatrix);
    const yAxis = new THREE.Vector3(...conic.yVector).applyMatrix3(linearMatrix);
    const xRadius = xAxis.length();
    const yRadius = yAxis.length();
    if (!Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(center.z)
      || !Number.isFinite(xRadius) || !Number.isFinite(yRadius) || xRadius <= 0 || yRadius <= 0) return null;
    xAxis.divideScalar(xRadius);
    yAxis.divideScalar(yRadius);
    // 非等比缩放会把圆变为椭圆；此时不能仍按原圆轴提供有效圆形引用。
    if (Math.abs(xRadius - yRadius) > Math.max(xRadius, yRadius) * CIRCLE_AXIS_RELATIVE_TOLERANCE
      || Math.abs(xAxis.dot(yAxis)) > CIRCLE_AXIS_RELATIVE_TOLERANCE) return null;
    return { center, normal: xAxis.cross(yAxis).normalize(), radius: (xRadius + yRadius) / 2 };
  }
}
